import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api';

/**
 * 6-step onboarding wizard (plan §9, D12+D23).
 *
 * The backend source-of-truth for progress is
 * ``app_settings.ONBOARDING_STEP`` + ``ONBOARDING_COMPLETED``. The
 * page calls ``/api/onboarding/start`` on mount and after every
 * action to pick up the server's opinion — never infers progress
 * from local state.
 *
 * Steps:
 *   1. tariff       — pick tariff + checkout (ЮКасса stub 501 for
 *                     now; the user sees a warning and can skip if
 *                     on a demo shop).
 *   2. keys         — CLIENT_ID + API_KEY (PROMO optional).
 *   3. sync         — trigger initial sync + show progress.
 *   4. skus         — pick active SKUs with quota counter.
 *   5. integrations — optional PROMO / Sellmonitor / bot.
 *   6. done         — mark completed + redirect.
 */

interface OnboardingState {
  step: string;
  completed: boolean;
  is_demo: boolean;
  billing_state: string | null;
  steps: string[];
}

interface ReadinessPayload {
  store_api_ready?: boolean;
  promo_ready?: boolean;
  notify_ready?: boolean;
  missing?: string[];
  entitlement?: {
    tariff_code?: string | null;
    billing_state?: string | null;
    sku_quota?: number | null;
    is_demo?: boolean;
  };
}

const STEP_LABELS: Record<string, string> = {
  tariff: 'Тариф и оплата',
  keys: 'Ключи Ozon API',
  sync: 'Первичная синхронизация',
  skus: 'Активные SKU',
  integrations: 'Дополнительные интеграции',
  done: 'Готово',
};

const STEP_HINTS: Record<string, string> = {
  tariff:
    'Выберите подписку и оплатите её через ЮКассу. Доступ к дашборду появится после подтверждения платежа.',
  keys:
    'Получите CLIENT_ID и API_KEY в кабинете Ozon Seller (Настройки → API). Без них мы не сможем загружать данные магазина.',
  sync:
    'Запускаем загрузку складов, групп товаров и истории запросов. Первая синхронизация занимает 5–15 минут.',
  skus:
    'Выберите SKU, по которым вы хотите получать рекомендации. Количество ограничено квотой тарифа.',
  integrations:
    'Необязательный шаг: подключите PROMO API, Sellmonitor и бот-уведомления.',
  done: 'Онбординг завершён — переходим к дашборду.',
};

interface OnboardingPageProps {
  onFinished: () => void;
}

export function OnboardingPage({ onFinished }: OnboardingPageProps) {
  const [state, setState] = useState<OnboardingState | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [actionMessage, setActionMessage] = useState<{
    text: string;
    kind: 'info' | 'success' | 'warning' | 'error';
  } | null>(null);
  const [readiness, setReadiness] = useState<ReadinessPayload | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const response = await apiFetch('/api/onboarding/start');
      if (response.status === 403) {
        setError('Онбординг доступен только владельцу магазина.');
        setLoading(false);
        return;
      }
      if (!response.ok) {
        setError(`Ошибка ${response.status}`);
        setLoading(false);
        return;
      }
      const body = (await response.json()) as OnboardingState;
      setState(body);
      setLoading(false);
      // Fetch readiness so the sync-step progress bar + integration
      // status are already populated on mount. Best-effort.
      try {
        const rres = await apiFetch('/api/settings/readiness');
        if (rres.ok) {
          setReadiness((await rres.json()) as ReadinessPayload);
        }
      } catch {
        /* readiness is non-critical */
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'unknown error');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // When the wizard flips to `done`, hand control back to Root so
  // the main app renders. We deliberately don't force a page
  // reload — that would re-mount the whole provider tree which
  // invalidates the in-progress entitlement cache.
  useEffect(() => {
    if (state?.completed) onFinished();
  }, [state?.completed, onFinished]);

  const callAction = useCallback(
    async (
      path: string,
      body: Record<string, unknown> | null,
      successMsg: string,
    ): Promise<void> => {
      setBusy(true);
      setActionMessage(null);
      try {
        const response = await apiFetch(path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: body == null ? undefined : JSON.stringify(body),
        });
        if (response.status === 501) {
          setActionMessage({
            text:
              'Оплата через ЮКассу временно недоступна. Обратитесь в поддержку.',
            kind: 'warning',
          });
          return;
        }
        if (response.status === 402) {
          let detail: Record<string, unknown> | null = null;
          try {
            detail = ((await response.json()) as { detail: unknown }).detail as Record<string, unknown>;
          } catch {
            detail = null;
          }
          const requested = Number(detail?.requested ?? 0);
          const allowed = Number(detail?.allowed ?? 0);
          setActionMessage({
            text: `Выбрано ${requested} SKU, но тариф позволяет только ${allowed}. Уберите лишние и попробуйте снова.`,
            kind: 'error',
          });
          return;
        }
        if (!response.ok) {
          let msg = `Ошибка ${response.status}`;
          try {
            const body = (await response.json()) as { detail?: unknown };
            if (typeof body.detail === 'string') msg = body.detail;
          } catch {
            /* keep fallback */
          }
          setActionMessage({ text: msg, kind: 'error' });
          return;
        }
        setActionMessage({ text: successMsg, kind: 'success' });
        await refresh();
      } finally {
        setBusy(false);
      }
    },
    [refresh],
  );

  const currentIndex = useMemo(() => {
    if (!state) return 0;
    return Math.max(0, state.steps.indexOf(state.step));
  }, [state]);

  if (loading) {
    return (
      <div style={container}>
        <h1>Настройка магазина</h1>
        <p>Загружаем прогресс…</p>
      </div>
    );
  }
  if (error || !state) {
    return (
      <div style={container}>
        <h1>Настройка магазина</h1>
        <p style={{ color: '#b91c1c' }}>{error ?? 'Не удалось загрузить онбординг.'}</p>
      </div>
    );
  }

  return (
    <div style={container}>
      <h1 style={{ marginBottom: 4 }}>Настройка магазина</h1>
      <p style={{ color: '#64748b', marginBottom: 24 }}>
        Шаг {currentIndex + 1} из {state.steps.length}: {STEP_LABELS[state.step] ?? state.step}
      </p>

      <StepRail steps={state.steps} currentIndex={currentIndex} />

      <section style={card}>
        <h2 style={{ marginTop: 0 }}>{STEP_LABELS[state.step] ?? state.step}</h2>
        <p style={{ color: '#475569', lineHeight: 1.5 }}>
          {STEP_HINTS[state.step] ?? ''}
        </p>

        {actionMessage && (
          <div
            role="status"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 8,
              background:
                actionMessage.kind === 'error'
                  ? '#fee2e2'
                  : actionMessage.kind === 'warning'
                    ? '#fef3c7'
                    : actionMessage.kind === 'success'
                      ? '#dcfce7'
                      : '#f1f5f9',
              color:
                actionMessage.kind === 'error'
                  ? '#991b1b'
                  : actionMessage.kind === 'warning'
                    ? '#92400e'
                    : actionMessage.kind === 'success'
                      ? '#166534'
                      : '#0f172a',
              fontSize: 14,
            }}
          >
            {actionMessage.text}
          </div>
        )}

        <div style={{ marginTop: 20 }}>
          {state.step === 'tariff' && (
            <TariffStep
              state={state}
              readiness={readiness}
              busy={busy}
              onSkipForDemo={() => {
                // Demo shops can't hit /billing so we advance past
                // the tariff step by submitting placeholder creds;
                // the user edits real values (or keeps the demo
                // placeholder) in the next step. We check BOTH
                // state.is_demo and the readiness-side flag — the
                // readiness fetch is best-effort and may be null if
                // it failed, but state.is_demo comes from
                // /api/onboarding/start and is always populated.
                const isDemoAuthoritative =
                  state.is_demo || readiness?.entitlement?.is_demo;
                void callAction(
                  '/api/onboarding/submit-store-keys',
                  {
                    client_id: isDemoAuthoritative ? 'demo' : '',
                    api_key: isDemoAuthoritative ? 'demo' : '',
                  },
                  'Шаг оплаты пропущен для demo-магазина.',
                );
              }}
              onGoToBilling={() => {
                window.location.hash = '#billing';
                onFinished();
              }}
            />
          )}
          {state.step === 'keys' && (
            <KeysStep busy={busy} onSubmit={(body) => callAction('/api/onboarding/submit-store-keys', body, 'Ключи сохранены.')} />
          )}
          {state.step === 'sync' && (
            <SyncStep
              readiness={readiness}
              busy={busy}
              onTrigger={() =>
                callAction(
                  '/api/onboarding/trigger-initial-sync',
                  null,
                  'Синхронизация запущена.',
                )
              }
            />
          )}
          {state.step === 'skus' && (
            <SkusStep
              busy={busy}
              onSubmit={(skus) =>
                callAction(
                  '/api/onboarding/submit-active-skus',
                  { active_skus: skus },
                  'Активные SKU сохранены.',
                )
              }
            />
          )}
          {state.step === 'integrations' && (
            <IntegrationsStep
              readiness={readiness}
              busy={busy}
              onAdvance={() =>
                callAction(
                  '/api/onboarding/complete',
                  null,
                  'Онбординг завершён.',
                )
              }
            />
          )}
          {state.step === 'done' && (
            <div>
              <p>Всё готово — сейчас перенаправим на дашборд.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface StepRailProps {
  steps: string[];
  currentIndex: number;
}
function StepRail({ steps, currentIndex }: StepRailProps) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap' }}>
      {steps.map((s, idx) => {
        const done = idx < currentIndex;
        const active = idx === currentIndex;
        return (
          <div
            key={s}
            style={{
              flex: '1 1 120px',
              padding: '8px 12px',
              borderRadius: 6,
              background: done ? '#dcfce7' : active ? '#dbeafe' : '#f1f5f9',
              color: done ? '#166534' : active ? '#1e3a8a' : '#64748b',
              fontSize: 13,
              fontWeight: active ? 700 : 500,
              textAlign: 'center',
              border: active ? '1px solid #2b6eff' : '1px solid transparent',
            }}
          >
            {idx + 1}. {STEP_LABELS[s] ?? s}
          </div>
        );
      })}
    </div>
  );
}

function TariffStep({
  state,
  readiness,
  busy,
  onGoToBilling,
  onSkipForDemo,
}: {
  state: OnboardingState;
  readiness: ReadinessPayload | null;
  busy: boolean;
  onGoToBilling: () => void;
  onSkipForDemo: () => void;
}) {
  const isDemo = state.is_demo || readiness?.entitlement?.is_demo;
  if (isDemo) {
    return (
      <div>
        <p>Demo-магазин — оплата не требуется.</p>
        <button type="button" style={btnPrimary} disabled={busy} onClick={onSkipForDemo}>
          Пропустить и продолжить
        </button>
      </div>
    );
  }
  return (
    <div>
      <p>
        Выберите тариф на странице оплаты. После подтверждения ЮКассой вы вернётесь сюда автоматически.
      </p>
      <button type="button" style={btnPrimary} onClick={onGoToBilling}>
        Перейти к тарифам
      </button>
    </div>
  );
}

function KeysStep({
  busy,
  onSubmit,
}: {
  busy: boolean;
  onSubmit: (body: Record<string, unknown>) => void | Promise<void>;
}) {
  const [clientId, setClientId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [storeName, setStoreName] = useState('');
  const [promoClientId, setPromoClientId] = useState('');
  const [promoSecret, setPromoSecret] = useState('');
  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault();
        void onSubmit({
          client_id: clientId,
          api_key: apiKey,
          store_name: storeName,
          promo_client_id: promoClientId,
          promo_client_secret: promoSecret,
        });
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 520 }}
    >
      <LabeledInput label="CLIENT_ID" value={clientId} onChange={setClientId} required />
      <LabeledInput label="API_KEY" value={apiKey} onChange={setApiKey} required type="password" />
      <LabeledInput label="Название магазина (необязательно)" value={storeName} onChange={setStoreName} />
      <details style={{ marginTop: 8 }}>
        <summary style={{ cursor: 'pointer', color: '#2b6eff' }}>
          Настройки PROMO API (необязательно)
        </summary>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          <LabeledInput label="PROMO_CLIENT_ID" value={promoClientId} onChange={setPromoClientId} />
          <LabeledInput
            label="PROMO_CLIENT_SECRET"
            value={promoSecret}
            onChange={setPromoSecret}
            type="password"
          />
        </div>
      </details>
      <button type="submit" style={btnPrimary} disabled={busy || !clientId || !apiKey}>
        Сохранить и продолжить
      </button>
    </form>
  );
}

function SyncStep({
  readiness,
  busy,
  onTrigger,
}: {
  readiness: ReadinessPayload | null;
  busy: boolean;
  onTrigger: () => void | Promise<void>;
}) {
  const ready = Boolean(readiness?.store_api_ready);
  return (
    <div>
      <p>
        Статус ключей Ozon:&nbsp;
        <strong style={{ color: ready ? '#166534' : '#92400e' }}>
          {ready ? 'готовы' : 'ожидаем подтверждения'}
        </strong>
      </p>
      <p style={{ color: '#64748b', fontSize: 13 }}>
        После запуска синхронизации данные начнут поступать в дашборд. Это может занять до 15 минут.
      </p>
      <button type="button" style={btnPrimary} disabled={busy} onClick={onTrigger}>
        Запустить синхронизацию
      </button>
    </div>
  );
}

function SkusStep({
  busy,
  onSubmit,
}: {
  busy: boolean;
  onSubmit: (skus: string[]) => void | Promise<void>;
}) {
  const [raw, setRaw] = useState('');
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestedCount, setSuggestedCount] = useState<number | null>(null);

  const fillFromTopRevenue = useCallback(async () => {
    setSuggestLoading(true);
    try {
      const response = await apiFetch(
        '/api/onboarding/suggest-active-skus?limit=50',
      );
      if (response.ok) {
        const body = (await response.json()) as { skus?: { sku: string }[] };
        const skus = (body.skus ?? [])
          .map((item) => item.sku)
          .filter((s): s is string => typeof s === 'string' && s.length > 0);
        setRaw(skus.join('\n'));
        setSuggestedCount(skus.length);
      }
    } finally {
      setSuggestLoading(false);
    }
  }, []);

  const parsedSkus = raw
    .split(/[\n,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 520 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          type="button"
          style={btnSecondary}
          onClick={() => void fillFromTopRevenue()}
          disabled={busy || suggestLoading}
        >
          {suggestLoading ? 'Загружаем…' : 'Заполнить топом по выручке'}
        </button>
        {suggestedCount != null && (
          <span style={{ color: '#64748b', fontSize: 13 }}>
            Заполнено {suggestedCount} SKU
          </span>
        )}
      </div>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 13, color: '#64748b' }}>
          Список SKU (по одному на строку или через запятую)
        </span>
        <textarea
          rows={10}
          style={{
            font: '13px/1.4 Menlo, monospace',
            border: '1px solid #cbd5e1',
            borderRadius: 6,
            padding: 8,
            resize: 'vertical',
          }}
          value={raw}
          onChange={(ev) => setRaw(ev.target.value)}
        />
        <span style={{ fontSize: 12, color: '#64748b' }}>
          Выбрано: {parsedSkus.length}
        </span>
      </label>
      <button
        type="button"
        style={btnPrimary}
        disabled={busy || parsedSkus.length === 0}
        onClick={() => void onSubmit(parsedSkus)}
      >
        Сохранить и продолжить
      </button>
    </div>
  );
}

function IntegrationsStep({
  readiness,
  busy,
  onAdvance,
}: {
  readiness: ReadinessPayload | null;
  busy: boolean;
  onAdvance: () => void | Promise<void>;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 520 }}>
      <ReadinessRow label="PROMO API" ok={Boolean(readiness?.promo_ready)} />
      <ReadinessRow label="Уведомления" ok={Boolean(readiness?.notify_ready)} />
      <ReadinessRow label="Store API" ok={Boolean(readiness?.store_api_ready)} />
      <p style={{ color: '#64748b', fontSize: 13 }}>
        Необязательные интеграции можно добавить позже в разделе «Настройки».
        Нажмите «Готово» для завершения онбординга.
      </p>
      <button type="button" style={btnPrimary} disabled={busy} onClick={onAdvance}>
        Готово
      </button>
    </div>
  );
}

function ReadinessRow({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ color: '#0f172a' }}>{label}</span>
      <span style={{ color: ok ? '#166534' : '#92400e', fontWeight: 600 }}>
        {ok ? 'настроено' : 'необязательно'}
      </span>
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  required,
  type,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: string;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 13, color: '#64748b' }}>{label}</span>
      <input
        type={type ?? 'text'}
        value={value}
        onChange={(ev) => onChange(ev.target.value)}
        required={required}
        style={{
          border: '1px solid #cbd5e1',
          borderRadius: 6,
          padding: '8px 10px',
          fontSize: 14,
        }}
      />
    </label>
  );
}

const container: React.CSSProperties = {
  padding: 24,
  maxWidth: 960,
  margin: '0 auto',
};

const card: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  padding: 20,
  background: '#fff',
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 16px',
  background: '#2b6eff',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};

const btnSecondary: React.CSSProperties = {
  padding: '8px 14px',
  background: '#fff',
  color: '#0f172a',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};
